import type { Components, Theme } from "@mui/material";

export const MuiAccordion: Components<Theme>["MuiAccordion"] = {
	defaultProps: {
		variant: "outlined",
	},
	variants: [
		{
			props: { variant: "outlined" },
			style: ({ theme }) => ({
				borderRadius: theme.spacing(2),
				borderColor: theme.palette.border,
				"&:before": { display: "none" },
				"&.Mui-expanded": { margin: 0 },
				"&:not(.Mui-expanded):hover": { background: theme.palette.muted.main },
				"&:first-of-type, &:last-of-type": { borderRadius: theme.spacing(2) },
				"&.Mui-disabled": {
					background: theme.palette.muted.main,
					pointerEvents: "none",
				},
			}),
		},
	],
};

export const MuiAccordionSummary: Components<Theme>["MuiAccordionSummary"] = {
	styleOverrides: {
		root: ({ theme }) => ({
			borderRadius: theme.spacing(2),
			minHeight: theme.spacing(5.5),
			paddingBlock: theme.spacing(1),
			paddingInline: theme.spacing(1.5),
			".MuiPaper-outlined.Mui-expanded &": {
				borderBottomColor: theme.palette.border,
				borderBottomStyle: "solid",
				borderBottomWidth: 1,
				borderBottomRightRadius: 0,
				borderBottomLeftRadius: 0,
				minHeight: theme.spacing(5.5),
			},
			"&.Mui-focusVisible": {
				outlineOffset: -1,
				outlineStyle: "solid",
				outlineWidth: 2,
			},
			".MuiAccordionSummary-content": {
				alignItems: "center",
				gap: theme.spacing(1.5),
				margin: 0,
				paddingRight: theme.spacing(1),
				"&.Mui-expanded": { margin: 0 },
				".MuiTypography-root": {
					fontWeight: 500,
				},
			},
			svg: {
				color: theme.palette.text.disabled,
				height: theme.spacing(2.75),
				width: theme.spacing(2.75),
			},
		}),
	},
	variants: [
		{
			// Leave `props` empty for default colors
			props: {},
			style: ({ theme }: { theme: Theme }) => ({
				fontSize: theme.typography.pxToRem(16),
				"&.Mui-expanded": {
					backgroundColor: theme.palette.muted.main,
				},
				"&.Mui-focusVisible": {
					backgroundColor: theme.palette.muted.main,
					outlineColor: theme.palette.primary.light,
				},
				"&:hover, &.Mui-expanded, &.Mui-focusVisible": {
					color: theme.palette.primary.main,
					svg: { color: theme.palette.primary.light },
				},
			}),
		},
		createAccordionSummaryColor("audiences"),
		createAccordionSummaryColor("creators"),
	],
};

export const MuiAccordionDetails: Components<Theme>["MuiAccordionDetails"] = {
	styleOverrides: {
		root: ({ theme }) => ({
			paddingBlock: theme.spacing(1.5),
		}),
	},
};

function createAccordionSummaryColor(color: "audiences" | "creators") {
	return {
		props: { color },
		style: ({ theme }: { theme: Theme }) => {
			const palette = theme.palette[color];
			return {
				"&.Mui-expanded": {
					backgroundColor: theme.palette.muted.main,
				},
				"&.Mui-focusVisible": {
					backgroundColor: palette.background,
					outlineColor: palette.light,
				},
				"&:hover, &.Mui-expanded, &.Mui-focusVisible": {
					svg: { color: palette.main },
				},
			};
		},
	};
}
